/**
 * Branded PDF Generator Utility
 * Reusable helper functions for creating client-branded PDFs
 */

import jsPDF from 'jspdf';
import { ColorPalette } from './colorPaletteGenerator';

export interface BrandingConfig {
  companyName: string;
  logo: string | null; // base64 image
  colorPalette: ColorPalette;
}

export class BrandedPDFGenerator {
  private pdf: jsPDF;
  private branding: BrandingConfig;
  private yPos: number;
  private pageWidth: number;
  private pageHeight: number;
  private margin: number;
  private contentWidth: number;
  private lineHeight: number;

  constructor(branding: BrandingConfig) {
    this.pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    this.branding = branding;
    this.yPos = 20;
    this.pageWidth = 210;
    this.pageHeight = 297;
    this.margin = 20;
    this.contentWidth = this.pageWidth - 2 * this.margin;
    this.lineHeight = 7;
  }

  /**
   * Get hex color as RGB array
   */
  private hexToRgb(hex: string): [number, number, number] {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [
          parseInt(result[1], 16),
          parseInt(result[2], 16),
          parseInt(result[3], 16)
        ]
      : [0, 0, 0];
  }

  /**
   * Check if page break is needed
   */
  checkPageBreak(requiredSpace: number): boolean {
    if (this.yPos + requiredSpace > this.pageHeight - this.margin) {
      this.pdf.addPage();
      this.yPos = this.margin;
      return true;
    }
    return false;
  }

  /**
   * Add a page break (start new page)
   */
  addPageBreak(): void {
    this.pdf.addPage();
    this.yPos = this.margin;
  }

  /**
   * Add branded header with logo and company name
   */
  addBrandedHeader(title: string, subtitle?: string): void {
    const primaryRgb = this.hexToRgb(this.branding.colorPalette.primary);

    // Background bar
    this.pdf.setFillColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
    this.pdf.rect(0, 0, this.pageWidth, 60, 'F');

    // Add logo if available (left side)
    const logoWidth = 30;
    const logoGap = 5; // Gap after logo
    const logoEndX = this.margin + logoWidth + logoGap;

    if (this.branding.logo) {
      try {
        this.pdf.addImage(this.branding.logo, 'PNG', this.margin, 10, logoWidth, 30);
      } catch (error) {
        console.warn('Could not add logo to PDF:', error);
      }
    }

    // Text centered in remaining space (after logo)
    const availableSpaceStart = this.branding.logo ? logoEndX : this.margin;
    const availableSpaceEnd = this.pageWidth - this.margin;
    const textX = availableSpaceStart + (availableSpaceEnd - availableSpaceStart) / 2;

    // Title - Center aligned
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(24);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(title, textX, 25, { align: 'center' });

    // Subtitle - Center aligned
    if (subtitle) {
      this.pdf.setFontSize(14);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text(subtitle, textX, 35, { align: 'center' });
    }

    // Company name - Center aligned
    this.pdf.setFontSize(12);
    this.pdf.text(
      this.branding.companyName,
      textX,
      subtitle ? 45 : 35,
      { align: 'center' }
    );

    this.yPos = 75;
  }

  /**
   * Add branded section header
   */
  addSectionHeader(title: string, icon: string = ''): void {
    this.checkPageBreak(12);

    const primaryRgb = this.hexToRgb(this.branding.colorPalette.primary);

    this.pdf.setFillColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
    this.pdf.rect(this.margin, this.yPos, this.contentWidth, 8, 'F');

    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(13);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(`${icon} ${title}`, this.margin + 3, this.yPos + 6);

    // Reset text color
    const textRgb = this.hexToRgb(this.branding.colorPalette.textPrimary);
    this.pdf.setTextColor(textRgb[0], textRgb[1], textRgb[2]);

    this.yPos += 12;
  }

  /**
   * Add subsection header with accent color
   */
  addSubsectionHeader(title: string): void {
    this.checkPageBreak(10);

    const accentRgb = this.hexToRgb(this.branding.colorPalette.accent);

    // Accent line
    this.pdf.setDrawColor(accentRgb[0], accentRgb[1], accentRgb[2]);
    this.pdf.setLineWidth(1);
    this.pdf.line(this.margin, this.yPos + 5, this.margin + 15, this.yPos + 5);

    // Text
    const textRgb = this.hexToRgb(this.branding.colorPalette.textPrimary);
    this.pdf.setTextColor(textRgb[0], textRgb[1], textRgb[2]);
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(title, this.margin + 18, this.yPos + 7);

    this.yPos += 12;
  }

  /**
   * Add text with wrapping
   */
  addText(text: string, fontSize: number = 10, isBold: boolean = false): void {
    const textRgb = this.hexToRgb(this.branding.colorPalette.textPrimary);

    this.pdf.setTextColor(textRgb[0], textRgb[1], textRgb[2]);
    this.pdf.setFontSize(fontSize);
    this.pdf.setFont('helvetica', isBold ? 'bold' : 'normal');

    const lines = this.pdf.splitTextToSize(text, this.contentWidth);
    lines.forEach((line: string) => {
      this.checkPageBreak(this.lineHeight);
      this.pdf.text(line, this.margin, this.yPos);
      this.yPos += this.lineHeight;
    });
  }

  /**
   * Add bullet point
   */
  addBullet(text: string, indent: number = 0, color?: string): void {
    this.checkPageBreak(this.lineHeight);

    if (color) {
      const rgb = this.hexToRgb(color);
      this.pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
    } else {
      const textRgb = this.hexToRgb(this.branding.colorPalette.textPrimary);
      this.pdf.setTextColor(textRgb[0], textRgb[1], textRgb[2]);
    }

    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'normal');

    // Bullet point
    this.pdf.text('•', this.margin + indent, this.yPos);

    // Text
    const lines = this.pdf.splitTextToSize(text, this.contentWidth - indent - 5);
    lines.forEach((line: string, index: number) => {
      if (index > 0) {
        this.checkPageBreak(this.lineHeight);
      }
      this.pdf.text(line, this.margin + indent + 5, this.yPos);
      this.yPos += this.lineHeight;
    });

    // Reset color
    const textRgb = this.hexToRgb(this.branding.colorPalette.textPrimary);
    this.pdf.setTextColor(textRgb[0], textRgb[1], textRgb[2]);
  }

  /**
   * Add key-value pair
   */
  addKeyValue(key: string, value: string): void {
    this.checkPageBreak(this.lineHeight);

    const textRgb = this.hexToRgb(this.branding.colorPalette.textPrimary);
    const primaryRgb = this.hexToRgb(this.branding.colorPalette.primary);

    this.pdf.setFontSize(10);

    // Key in primary color
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
    this.pdf.text(`${key}:`, this.margin, this.yPos);

    // Fixed offset for column alignment - all values start at same position
    const valueOffset = 70; // 70mm to accommodate long keys

    // Value in text color
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setTextColor(textRgb[0], textRgb[1], textRgb[2]);

    const lines = this.pdf.splitTextToSize(value, this.contentWidth - valueOffset);
    lines.forEach((line: string, index: number) => {
      if (index > 0) {
        this.checkPageBreak(this.lineHeight);
        this.pdf.text(line, this.margin + valueOffset, this.yPos);
        this.yPos += this.lineHeight;
      } else {
        this.pdf.text(line, this.margin + valueOffset, this.yPos);
        this.yPos += this.lineHeight;
      }
    });
  }

  /**
   * Add colored box with text
   */
  addColoredBox(text: string, backgroundColor: string, textColor: string = '#FFFFFF'): void {
    this.checkPageBreak(15);

    const bgRgb = this.hexToRgb(backgroundColor);
    const txtRgb = this.hexToRgb(textColor);

    this.pdf.setFillColor(bgRgb[0], bgRgb[1], bgRgb[2]);
    this.pdf.rect(this.margin, this.yPos, this.contentWidth, 12, 'F');

    this.pdf.setTextColor(txtRgb[0], txtRgb[1], txtRgb[2]);
    this.pdf.setFontSize(11);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(text, this.margin + 3, this.yPos + 8);

    // Reset text color
    const textRgb = this.hexToRgb(this.branding.colorPalette.textPrimary);
    this.pdf.setTextColor(textRgb[0], textRgb[1], textRgb[2]);

    this.yPos += 17;
  }

  /**
   * Add horizontal line (separator)
   */
  addSeparator(color?: string): void {
    this.checkPageBreak(5);

    const lineColor = color || this.branding.colorPalette.neutral;
    const rgb = this.hexToRgb(lineColor);

    this.pdf.setDrawColor(rgb[0], rgb[1], rgb[2]);
    this.pdf.setLineWidth(0.5);
    this.pdf.line(this.margin, this.yPos, this.pageWidth - this.margin, this.yPos);

    this.yPos += 5;
  }

  /**
   * Add space
   */
  addSpace(height: number = 5): void {
    this.yPos += height;
  }

  /**
   * Add branded footer to all pages
   */
  addBrandedFooter(reportTitle: string): void {
    const pageCount = (this.pdf as any).internal.getNumberOfPages();
    const secondaryRgb = this.hexToRgb(this.branding.colorPalette.textSecondary);

    for (let i = 1; i <= pageCount; i++) {
      this.pdf.setPage(i);
      this.pdf.setFontSize(8);
      this.pdf.setTextColor(secondaryRgb[0], secondaryRgb[1], secondaryRgb[2]);

      // Footer text
      const footerText = `${this.branding.companyName} | ${reportTitle} | Page ${i} of ${pageCount} | ${new Date().toLocaleDateString()}`;
      this.pdf.text(footerText, this.pageWidth / 2, this.pageHeight - 10, { align: 'center' });

      // Footer line
      const primaryRgb = this.hexToRgb(this.branding.colorPalette.primary);
      this.pdf.setDrawColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
      this.pdf.setLineWidth(0.5);
      this.pdf.line(this.margin, this.pageHeight - 15, this.pageWidth - this.margin, this.pageHeight - 15);
    }
  }

  /**
   * Get the PDF instance
   */
  getPDF(): jsPDF {
    return this.pdf;
  }

  /**
   * Save the PDF
   */
  save(filename: string): void {
    this.pdf.save(filename);
  }

  /**
   * Get current Y position
   */
  getYPos(): number {
    return this.yPos;
  }

  /**
   * Set Y position
   */
  setYPos(y: number): void {
    this.yPos = y;
  }

  /**
   * Get branding config
   */
  getBranding(): BrandingConfig {
    return this.branding;
  }
}

/**
 * Helper function to convert RGB to Hex
 */
export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}
